import { GuaranteeDto } from 'api/pricing';
import { CardsSection } from 'components/layout/Section/CardsSection';
import { useDynamicTranslation } from 'hooks/useDynamicTranslation';
import { NsKey } from 'enums/NsKey';
import { FC, useCallback } from 'react';
import { BorrowerGuaranteeCardItem } from './BorrowerGuaranteeCardItem';

interface GuaranteesListProps {
  requiredGuarantees: GuaranteeDto[];
  optionalGuarantees: GuaranteeDto[];
  activeGuaranteeIds: string[];
  isActive: (guaranteeId: string) => boolean;
  isAvailable: (guarantee: GuaranteeDto) => boolean;
  getMissingDeps: (guarantee: GuaranteeDto) => string[];
  onToggle: (guarantee: GuaranteeDto) => void;
  onShowDetails: (title: string, description: string) => void;
}

export const GuaranteesList: FC<GuaranteesListProps> = ({
  requiredGuarantees,
  optionalGuarantees,
  isActive,
  isAvailable,
  getMissingDeps,
  onToggle,
  onShowDetails
}) => {
  const { t } = useDynamicTranslation([NsKey.Pricing]);

  const renderGuaranteeCard = useCallback((guarantee: GuaranteeDto) => {
    const guaranteeIsActive = isActive(guarantee.id);
    const guaranteeIsAvailable = isAvailable(guarantee);
    const missingDeps = !guaranteeIsAvailable ? getMissingDeps(guarantee) : [];

    return (
      <BorrowerGuaranteeCardItem
        key={guarantee.name}
        isActive={guaranteeIsActive}
        guarantee={guarantee}
        isRequired={guarantee.isRequired}
        isUnavailableDueToDependencies={!guarantee.isRequired && !guaranteeIsAvailable && !guaranteeIsActive}
        missingDependencies={missingDeps}
        toggleGuarantee={() => onToggle(guarantee)}
        showGuaranteeDetails={onShowDetails}
      />
    );
  }, [isActive, isAvailable, getMissingDeps, onToggle, onShowDetails]);

  return (
    <>
      {requiredGuarantees.length > 0 && (
        <CardsSection title={t('PRICING.GUARANTEES.MANDATORY')}>
          <div className="flex flex-col gap-4">
            {requiredGuarantees.map(renderGuaranteeCard)}
          </div>
        </CardsSection>
      )}

      {optionalGuarantees.length > 0 && (
        <CardsSection title={t('PRICING.GUARANTEES.OPTIONAL')}>
          <div className="flex flex-col gap-4">
            {optionalGuarantees.map(renderGuaranteeCard)}
          </div>
        </CardsSection>
      )}
    </>
  );
};
